import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_BackgroundImage = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🖼 CSS Background Image</h1>

      <p className="tutorial-intro">
        The <code>background-image</code> property sets an image as the
        background of an element. It can be combined with{" "}
        <strong>position, repeat, size, and attachment</strong> for creative
        effects.
      </p>

      {/* Basic Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Basic Syntax</h2>
        <CodeCopyBlock
          code={`div {
  background-image: url("image.jpg");
}`}
        />
        <div
          style={{
            backgroundImage: "url('https://picsum.photos/400/150?random=10')",
            padding: "2rem",
            borderRadius: "8px",
            marginTop: "1rem",
            color: "white",
            fontWeight: "bold",
          }}
        >
          This box has a background image.
        </div>
      </div>

      {/* Background Repeat */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Background Repeat</h2>
        <p>
          The <code>background-repeat</code> property specifies how the image is
          repeated.
        </p>
        <CodeCopyBlock
          code={`div {
  background-image: url("pattern.png");
  background-repeat: no-repeat;   /* no repeat */
  background-repeat: repeat-x;    /* repeat horizontally */
  background-repeat: repeat-y;    /* repeat vertically */
}`}
        />
        <div
          style={{
            backgroundImage:
              "url('https://www.transparenttextures.com/patterns/hexellence.png')",
            backgroundRepeat: "repeat-x",
            padding: "3rem",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        >
          Image repeated <strong>horizontally</strong>.
        </div>
      </div>

      {/* Background Position */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Background Position</h2>
        <p>
          Defines where the background image is placed inside the element. It
          accepts values like <code>top</code>, <code>center</code>,{" "}
          <code>bottom</code>, or coordinates.
        </p>
        <CodeCopyBlock
          code={`div {
  background-image: url("logo.png");
  background-repeat: no-repeat;
  background-position: center; /* or left top, right bottom */
}`}
        />
        <div
          style={{
            backgroundImage:
              "url('https://upload.wikimedia.org/wikipedia/commons/a/a7/React-icon.svg')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "60px",
            padding: "4rem",
            borderRadius: "8px",
            border: "1px solid #ddd",
            marginTop: "1rem",
          }}
        >
          Background image positioned at <strong>center</strong>.
        </div>
      </div>

      {/* Background Size */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Background Size</h2>
        <p>
          Specifies how the image should fit inside the element. Options include{" "}
          <code>auto</code>, <code>cover</code>, and <code>contain</code>.
        </p>
        <CodeCopyBlock
          code={`div {
  background-image: url("nature.jpg");
  background-size: cover;   /* fills entire element */
  background-size: contain; /* fits inside element */
}`}
        />
        <div
          style={{
            backgroundImage: "url('https://picsum.photos/400/200?random=20')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            height: "150px",
            borderRadius: "8px",
            marginTop: "1rem",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
          }}
        >
          Background size set to <strong>cover</strong>.
        </div>
      </div>

      {/* Fixed Background */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Fixed Background</h2>
        <p>
          With <code>background-attachment: fixed</code>, the background image
          stays fixed when you scroll.
        </p>
        <CodeCopyBlock
          code={`div {
  background-image: url("mountain.jpg");
  background-attachment: fixed;
  background-size: cover;
}`}
        />
        <div
          style={{
            backgroundImage: "url('https://picsum.photos/800/400?random=30')",
            backgroundAttachment: "fixed",
            backgroundSize: "cover",
            height: "200px",
            borderRadius: "8px",
            marginTop: "1rem",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
          }}
        >
          Scroll to see fixed background effect.
        </div>
      </div>

      {/* Multiple Backgrounds */}
      <div className="tutorial-section">
        <h2 className="section-title">6️⃣ Multiple Backgrounds</h2>
        <p>
          You can apply more than one background image by separating them with
          commas.
        </p>
        <CodeCopyBlock
          code={`div {
  background-image: url("stars.png"), url("sky.jpg");
  background-position: center, top;
  background-repeat: no-repeat, repeat;
}`}
        />
        <div
          style={{
            backgroundImage:
              "url('https://upload.wikimedia.org/wikipedia/commons/3/3f/Starsinthesky.jpg'), url('https://picsum.photos/400/200?random=40')",
            backgroundSize: "contain, cover",
            backgroundRepeat: "no-repeat, no-repeat",
            backgroundPosition: "center, center",
            height: "200px",
            borderRadius: "8px",
            marginTop: "1rem",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
            textShadow: "0px 0px 5px black",
          }}
        >
          Multiple background images
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Mastering <code>background-image</code> helps you create visually rich
          layouts. Next, explore{" "}
          <a href="/css/backgrounds" className="learn-more">
            CSS Backgrounds
          </a>{" "}
          to learn more about combining background properties creatively.
        </p>
      </div>
    </div>
  );
};

export default CSS_BackgroundImage;
